/**
 * FSD Part 8 — Manufacturing. Sections follow the FSD's feature
 * specifications 8.2.1–8.2.8 (MFG-F01…MFG-F08) in order. Quality (`internal/
 * qms`) lives in 8.2.7 (MFG-F07): the FSD specifies quality management inside
 * Manufacturing, even though inspections are also raised from receiving and
 * despatch. No current screen maps to 8.2.6, so it has no section here.
 *
 * Item ids are stable and must never change: favourites and recents are
 * keyed off them in localStorage. Section ids (`s-<fsd>`) are new in C17.
 *
 * Detail routes (`/mfg/production-orders/:id`, `/mfg/boms/:id`,
 * `/mfg/routings/:id`, one MRP run and its pegging tree, one roll-up) are
 * absent: they are reached from the list or document that owns them.
 */
import type { NavGroup } from '../types'
import { MFG_PERMISSIONS } from '@/modules/mfg/permissions'
import { QMS_PERMISSIONS } from '@/modules/qms/permissions'

export const P08_MANUFACTURING_GROUPS: NavGroup[] = [
  {
    id: 's-8.2.1',
    kind: 'feature',
    fsd: '8.2.1',
    code: 'MFG-F01',
    labelKey: 'nav.fsd.s8_2_1.short',
    fullTitleKey: 'nav.fsd.s8_2_1.full',
    items: [
      {
        id: 'mfg-boms',
        labelKey: 'mfg.routeTitles.boms',
        to: '/mfg/boms',
        icon: 'link',
        permission: MFG_PERMISSIONS.bomRead,
        keywordsKey: 'nav.items.mfgBoms.keywords',
        descriptionKey: 'nav.items.mfgBoms.description',
      },
      {
        id: 'mfg-ecos',
        labelKey: 'mfg.c7.routeTitles.ecos',
        to: '/mfg/engineering-change-orders',
        icon: 'sitemap',
        permission: MFG_PERMISSIONS.ecoRead,
      },
    ],
  },
  {
    id: 's-8.2.2',
    kind: 'feature',
    fsd: '8.2.2',
    code: 'MFG-F02',
    labelKey: 'nav.fsd.s8_2_2.short',
    fullTitleKey: 'nav.fsd.s8_2_2.full',
    items: [
      {
        id: 'mfg-routings',
        labelKey: 'mfg.routeTitles.routings',
        to: '/mfg/routings',
        icon: 'refresh',
        permission: MFG_PERMISSIONS.routingRead,
        keywordsKey: 'nav.items.mfgRoutings.keywords',
        descriptionKey: 'nav.items.mfgRoutings.description',
      },
      {
        id: 'mfg-work-centers',
        labelKey: 'mfg.routeTitles.workCenters',
        to: '/mfg/work-centers',
        icon: 'building',
        permission: MFG_PERMISSIONS.workCenterRead,
        keywordsKey: 'nav.items.mfgWorkCenters.keywords',
        descriptionKey: 'nav.items.mfgWorkCenters.description',
      },
      {
        id: 'mfg-tools',
        labelKey: 'mfg.c7.routeTitles.tools',
        to: '/mfg/tools',
        icon: 'monitor',
        permission: MFG_PERMISSIONS.capacityRead,
      },
    ],
  },
  {
    id: 's-8.2.3',
    kind: 'feature',
    fsd: '8.2.3',
    code: 'MFG-F03',
    labelKey: 'nav.fsd.s8_2_3.short',
    fullTitleKey: 'nav.fsd.s8_2_3.full',
    items: [
      {
        id: 'mfg-mrp',
        labelKey: 'mfg.routeTitles.mrp',
        to: '/mfg/mrp',
        icon: 'star',
        permission: MFG_PERMISSIONS.mrpRead,
        keywordsKey: 'nav.items.mfgMrp.keywords',
        descriptionKey: 'nav.items.mfgMrp.description',
      },
      {
        id: 'mfg-planning-parameters',
        labelKey: 'mfg.c7.routeTitles.planningParameters',
        to: '/mfg/planning-parameters',
        icon: 'settings',
        permission: MFG_PERMISSIONS.planningManage,
      },
    ],
  },
  {
    id: 's-8.2.4',
    kind: 'feature',
    fsd: '8.2.4',
    code: 'MFG-F04',
    labelKey: 'nav.fsd.s8_2_4.short',
    fullTitleKey: 'nav.fsd.s8_2_4.full',
    items: [
      {
        id: 'mfg-capacity',
        labelKey: 'mfg.routeTitles.capacity',
        to: '/mfg/reports/capacity',
        icon: 'clock',
        permission: MFG_PERMISSIONS.reportRead,
        keywordsKey: 'nav.items.mfgCapacity.keywords',
        descriptionKey: 'nav.items.mfgCapacity.description',
      },
      {
        id: 'mfg-schedule-runs',
        labelKey: 'mfg.c7.routeTitles.scheduleRuns',
        to: '/mfg/schedule-runs',
        icon: 'calendar',
        permission: MFG_PERMISSIONS.capacityRead,
      },
      {
        id: 'mfg-bottlenecks',
        labelKey: 'mfg.c7.routeTitles.bottlenecks',
        to: '/mfg/reports/bottlenecks',
        icon: 'alert',
        permission: MFG_PERMISSIONS.capacityRead,
      },
    ],
  },
  {
    id: 's-8.2.5',
    kind: 'feature',
    fsd: '8.2.5',
    code: 'MFG-F05',
    labelKey: 'nav.fsd.s8_2_5.short',
    fullTitleKey: 'nav.fsd.s8_2_5.full',
    items: [
      {
        id: 'mfg-production-orders',
        labelKey: 'mfg.routeTitles.productionOrders',
        to: '/mfg/production-orders',
        icon: 'inbox',
        permission: MFG_PERMISSIONS.orderRead,
        keywordsKey: 'nav.items.mfgProductionOrders.keywords',
        descriptionKey: 'nav.items.mfgProductionOrders.description',
      },
    ],
  },
  {
    id: 's-8.2.7',
    kind: 'feature',
    fsd: '8.2.7',
    code: 'MFG-F07',
    labelKey: 'nav.fsd.s8_2_7.short',
    fullTitleKey: 'nav.fsd.s8_2_7.full',
    items: [
      {
        id: 'qms-plans',
        labelKey: 'qms.routeTitles.plans',
        to: '/qms/inspection-plans',
        icon: 'shieldCheck',
        permission: QMS_PERMISSIONS.planRead,
      },
      {
        id: 'qms-sampling-plans',
        labelKey: 'qms.routeTitles.samplingPlans',
        to: '/qms/sampling-plans',
        icon: 'scale',
        permission: QMS_PERMISSIONS.planRead,
      },
      {
        id: 'qms-worklist',
        labelKey: 'qms.routeTitles.worklist',
        to: '/qms/inspections',
        icon: 'clock',
        permission: QMS_PERMISSIONS.inspectionRead,
      },
      {
        id: 'qms-capa-board',
        labelKey: 'qms.routeTitles.capaBoard',
        to: '/qms/capa',
        icon: 'alert',
        permission: QMS_PERMISSIONS.capaRead,
      },
      {
        id: 'qms-quality-summary',
        labelKey: 'qms.routeTitles.qualitySummary',
        to: '/qms/reports/quality-summary',
        icon: 'dashboard',
        permission: QMS_PERMISSIONS.inspectionRead,
      },
      {
        id: 'qms-settings',
        labelKey: 'qms.routeTitles.settings',
        to: '/qms/settings',
        icon: 'settings',
        permission: QMS_PERMISSIONS.planRead,
      },
    ],
  },
  {
    id: 's-8.2.8',
    kind: 'feature',
    fsd: '8.2.8',
    code: 'MFG-F08',
    labelKey: 'nav.fsd.s8_2_8.short',
    fullTitleKey: 'nav.fsd.s8_2_8.full',
    items: [
      {
        id: 'mfg-wip-clearing',
        labelKey: 'mfg.routeTitles.wipClearing',
        to: '/mfg/reports/wip-clearing',
        icon: 'check',
        permission: MFG_PERMISSIONS.reportRead,
        keywordsKey: 'nav.items.mfgWipClearing.keywords',
        descriptionKey: 'nav.items.mfgWipClearing.description',
      },
      {
        // `standardCostRead` and `settingsRead` are separate from their manage
        // counterparts on the server: the standard cost an order is measured
        // against, and the WIP and variance accounts it posts to, can be read
        // by the people held to them without being editable.
        id: 'mfg-standard-costs',
        labelKey: 'mfg.routeTitles.standardCosts',
        to: '/mfg/standard-costs',
        icon: 'dashboard',
        permission: MFG_PERMISSIONS.standardCostRead,
        keywordsKey: 'nav.items.mfgStandardCosts.keywords',
        descriptionKey: 'nav.items.mfgStandardCosts.description',
      },
      {
        id: 'mfg-settings',
        labelKey: 'mfg.routeTitles.settings',
        to: '/mfg/settings',
        icon: 'settings',
        permission: MFG_PERMISSIONS.settingsRead,
        keywordsKey: 'nav.items.mfgSettings.keywords',
        descriptionKey: 'nav.items.mfgSettings.description',
      },
      {
        id: 'mfg-rollups',
        labelKey: 'mfg.c7.routeTitles.rollups',
        to: '/mfg/standard-cost-rollups',
        icon: 'history',
        permission: MFG_PERMISSIONS.standardCostRead,
      },
      {
        id: 'mfg-variance-analysis',
        labelKey: 'mfg.c7.routeTitles.varianceAnalysis',
        to: '/mfg/reports/variance-analysis',
        icon: 'dashboard',
        permission: MFG_PERMISSIONS.varianceRead,
      },
    ],
  },
]
